import React from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import SectionHeader from 'new/elements/SectionHeader/SectionHeader';
import {
  formatDateRangeForApi,
  MetricsDateControls,
  MetricsTrendPanel,
  MetricsDateRange,
  ResourceMetricsData,
} from '@modules/monitor/resource-triage/components/MetricsTrendPanel';
import { useFlowDailyMetricsRepository } from 'new/state/metrics/repository/useFlowDailyMetricsRepository';
import type { DailyMetrics } from 'new/stores/metrics/metrics.endpoints';
import FlowKpiSummary from './FlowKpiSummary';
import styles from './FlowDailyMetrics.module.scss';

interface FlowDailyMetricsProps {
  originNodeId: number | string;
  start: Date;
  end: Date;
  hideDateControls?: boolean;
  hideRecords?: boolean;
  showUtcChartLabel?: boolean;
  useLocalCalendarDays?: boolean;
  onDatesChanged?: (dates: MetricsDateRange) => void;
  dateControls?: React.ReactNode;
}

const UTC_CHART_TIMEZONE_LABEL = 'Chart data is shown in UTC.';

function formatLocalCalendarDay(value: Date): string {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

function formatDateRangeForDailyMetrics(
  range: { start: Date; end: Date },
  useLocalCalendarDays: boolean,
) {
  if (!useLocalCalendarDays) return formatDateRangeForApi(range);

  return {
    start: formatLocalCalendarDay(range.start),
    end: formatLocalCalendarDay(range.end),
  };
}

function toResourceMetricsData(metrics: DailyMetrics): ResourceMetricsData {
  return {
    metrics: Object.fromEntries(metrics.map((metric) => [metric.time, metric])),
  };
}

const FlowDailyMetrics = ({
  originNodeId,
  start,
  end,
  hideDateControls = true,
  hideRecords = false,
  showUtcChartLabel = false,
  useLocalCalendarDays = false,
  onDatesChanged,
  dateControls,
}: FlowDailyMetricsProps) => {
  /**
   * Always compute the range ourselves instead of accepting a caller-supplied {from, to}.
   *
   * The daily metrics backend truncates both bounds to UTC start-of-day,
   * so a timezone-localized timestamp — like the one FlowInsights builds for the /metrics run-summary call
   *  — would land on the wrong UTC day and quietly clip hours off the intended local day.
   */
  const apiRange = React.useMemo(
    () => formatDateRangeForDailyMetrics({ start, end }, useLocalCalendarDays),
    [start, end, useLocalCalendarDays],
  );

  const { data, isLoading, isFetching } = useFlowDailyMetricsRepository(originNodeId, {
    from: apiRange.start,
    to: apiRange.end,
  });

  const loading = isLoading || isFetching;
  const handleDatesChanged = React.useCallback(
    (dates: MetricsDateRange) => onDatesChanged?.(dates),
    [onDatesChanged],
  );

  const dateControlsContent =
    dateControls ||
    (!hideDateControls ? (
      <MetricsDateControls start={start} end={end} onDatesChanged={handleDatesChanged} />
    ) : null);

  const metricsData = React.useMemo(() => (data ? toResourceMetricsData(data) : null), [data]);

  const totals = React.useMemo(() => {
    if (!data) return { records: 0, errors: 0, size: 0 };
    return data.reduce(
      (acc, metric) => ({
        records: acc.records + (metric.records || 0),
        errors: acc.errors + (metric.errors || 0),
        size: acc.size + (metric.size || 0),
      }),
      { records: 0, errors: 0, size: 0 },
    );
  }, [data]);

  return (
    <Box display="flex" flexDirection="column" gap={2} px={2} pt={2} pb={1}>
      <SectionHeader
        title="Recent Usage Trends"
        subtitle={
          hideRecords
            ? 'Aggregated totals and daily trends for errors and data volume over the selected period.'
            : 'Aggregated totals and daily trends for records processed, errors, and data volume over the selected period.'
        }
      />

      {dateControlsContent ? (
        <Box display="flex" justifyContent="flex-end">
          {dateControlsContent}
        </Box>
      ) : null}

      <FlowKpiSummary
        records={totals.records}
        errors={totals.errors}
        size={totals.size}
        loading={loading}
        hideRecords={hideRecords}
      />
      <MetricsTrendPanel
        data={metricsData}
        loading={loading}
        start={apiRange.start}
        end={apiRange.end}
        onDatesChanged={handleDatesChanged}
        hideRecords={hideRecords}
        hideDateControls
        hideTitle
      />
      {showUtcChartLabel ? (
        <Box display="flex" justifyContent="flex-end">
          {loading ? (
            <Skeleton animation="wave" variant="rounded" className={styles.utcLabelSkeleton} />
          ) : (
            <Typography variant="caption" color="text.secondary">
              {UTC_CHART_TIMEZONE_LABEL}
            </Typography>
          )}
        </Box>
      ) : null}
    </Box>
  );
};

FlowDailyMetrics.displayName = 'FlowDailyMetrics';

export default FlowDailyMetrics;
